import { createContext, ReactElement, useState, useEffect } from 'react';

// project import
import config, { MenuOrientation, ThemeDirection, ThemeMode, TextSize } from 'config';
import useLocalStorage from 'hooks/useLocalStorage';

// types
import { CustomizationProps, FontFamily, I18n, PresetColor, OrdersFilters } from 'types/config';

// initial state
const initialState: CustomizationProps = {
  ...config,
  onChangeContainer: () => {},
  onChangeLocalization: () => {},
  onChangeMode: () => {},
  onChangePresetColor: () => {},
  onChangeDirection: () => {},
  onChangeMiniDrawer: () => {},
  onChangeMenuOrientation: () => {},
  onChangeFontFamily: () => {},
  onChangeTextSize: () => {},
  isCompactView: false,
  onIsComactViewChange: () => {},

  // New view mode flags
  isOrdersGridView: false,
  onIsOrdersGridViewChange: () => {},

  ordersTabValue: 1,
  onOrdersTabValueChange: () => {},

  ordersCreatedAfterDateTimestamp: "",
  ordersCreatedAfterDate: new Date(),
  onOrdersCreatedAfterDateChange: () => {},

  ordersFilters: {
    instrument: "",
    portfolioManager: "",
    country: "",
    status: "",
    fund: "",
    workingTrader: "",
    ordersSearch: "",
    hasUserChangedPM: false
  },
  onOrdersFiltersChange: () => {},
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
  children: ReactElement;
};

function ConfigProvider({ children }: ConfigProviderProps) {
  const [storedConfig, setStoredConfig] = useLocalStorage('mantis-react-ts-config', initialState);

  // Function to apply text size globally
  const applyTextSize = (textSize: TextSize) => {
    const root = document.documentElement;
    
    // Define text size scale factors - adjusted to match original baseline
    const sizeMap = {
      [TextSize.NONE]: 1.0,      // 100% of base size (original pre-feature size)
      [TextSize.SMALL]: 0.80,    // 80% of base size (10% smaller than medium)
      [TextSize.MEDIUM]: 0.90,   // 90% of base size 
      [TextSize.LARGE]: 1.10     // 110% of base size
    };
    
    const scale = sizeMap[textSize];
    root.style.setProperty('--text-size-scale', scale.toString());
  };

  const timestampStr = storedConfig.ordersCreatedAfterDateTimestamp;
  let ordersCreatedAfterDate = storedConfig.ordersCreatedAfterDate;

  const updateOrdersCreatedAfterDate = () => {
    setStoredConfig({
      ...storedConfig,
      ordersCreatedAfterDate:  new Date(),
      ordersCreatedAfterDateTimestamp: new Date().toISOString()
    });
  }

  if (timestampStr) {
    const savedDate = new Date(timestampStr);
    const now = new Date();

    // If saved date is from a previous calendar day, reset to today's date
    const savedYMD = [savedDate.getFullYear(), savedDate.getMonth(), savedDate.getDate()];
    const nowYMD = [now.getFullYear(), now.getMonth(), now.getDate()];
    let isPreviousDay =
      savedYMD[0] < nowYMD[0] ||
      savedYMD[1] < nowYMD[1] ||
      savedYMD[2] < nowYMD[2];

    if (isPreviousDay === true) {
      updateOrdersCreatedAfterDate();
    }
  } else {
    updateOrdersCreatedAfterDate();
  }

  // Merge any missing ordersFilters properties with empty strings
  const mergedConfig = {
    ...storedConfig,
    ordersCreatedAfterDate,
    ordersFilters: {
      ...initialState.ordersFilters,  // default empty strings
      ...(storedConfig.ordersFilters ?? {}) // stored values, if any
    }
  };

  // Apply text size on mount and when stored config changes
  useEffect(() => {
    applyTextSize(mergedConfig.textSize || TextSize.NONE);
  }, [mergedConfig.textSize]);

  const onChangeContainer = () => {
    setStoredConfig({
      ...mergedConfig,
      container: !mergedConfig.container
    });
  };

  const onChangeLocalization = (lang: I18n) => {
    setStoredConfig({
      ...mergedConfig,
      i18n: lang
    });
  };

  const onChangeMode = (mode: ThemeMode) => {
    setStoredConfig({
      ...mergedConfig,
      mode
    });
  };

  const onChangePresetColor = (theme: PresetColor) => {
    setStoredConfig({
      ...mergedConfig,
      presetColor: theme
    });
  };

  const onChangeDirection = (direction: ThemeDirection) => {
    setStoredConfig({
      ...mergedConfig,
      themeDirection: direction
    });
  };

  const onChangeMiniDrawer = (miniDrawer: boolean) => {
    setStoredConfig({
      ...mergedConfig,
      miniDrawer
    });
  };

  const onChangeMenuOrientation = (layout: MenuOrientation) => {
    setStoredConfig({
      ...mergedConfig,
      menuOrientation: layout
    });
  };

  const onChangeFontFamily = (fontFamily: FontFamily) => {
    setStoredConfig({
      ...mergedConfig,
      fontFamily
    });
  };

  const onChangeTextSize = (textSize: TextSize) => {
    setStoredConfig({
      ...mergedConfig,
      textSize
    });
    
    // Apply text size immediately to the document
    applyTextSize(textSize);
  };

  const onIsComactViewChange = (currentSetting: boolean) => {
    setStoredConfig({
      ...mergedConfig,
      isCompactView: currentSetting
    });
  };

  const onIsOrdersGridViewChange = (value: boolean) => {
    setStoredConfig({
      ...mergedConfig,
      isOrdersGridView: value
    });
  };

  const onOrdersTabValueChange = (newVal: number) => {
    setStoredConfig({
      ...mergedConfig,
      ordersTabValue: newVal
    });
  };

  const onOrdersCreatedAfterDateChange = (newDate?: Date) => {
    setStoredConfig({
      ...mergedConfig,
      ordersCreatedAfterDate: newDate ?? null,
      ordersCreatedAfterDateTimestamp: new Date().toISOString()
    });
  };

  const onOrdersFiltersChange = (newFilters: Partial<OrdersFilters>) => {
    setStoredConfig({
      ...mergedConfig,
      ordersFilters: {
        ...mergedConfig.ordersFilters,
        ...newFilters
      }
    });
  };

  return (
    <ConfigContext.Provider
      value={{
        ...mergedConfig,
        onChangeContainer,
        onChangeLocalization,
        onChangeMode,
        onChangePresetColor,
        onChangeDirection,
        onChangeMiniDrawer,
        onChangeMenuOrientation,
        onChangeFontFamily,
        onChangeTextSize,
        onIsComactViewChange,

        // Expose new props
        isOrdersGridView: mergedConfig.isOrdersGridView,
        onIsOrdersGridViewChange,

        ordersTabValue: mergedConfig.ordersTabValue,
        onOrdersTabValueChange,

        ordersCreatedAfterDate: mergedConfig.ordersCreatedAfterDate,
        onOrdersCreatedAfterDateChange,

        ordersFilters: mergedConfig.ordersFilters,
        onOrdersFiltersChange
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export { ConfigProvider, ConfigContext };
